import { useState } from 'react';
import { Link } from 'react-router-dom';
import { payNow } from '../lib/payments';
import { errorMessage } from '../lib/http';
import { formatMoney } from '../lib/format';
import { BTN_PRIMARY, BTN_PRIMARY_STYLE } from '../lib/ui';
import type { Payment } from '../types/workspace';
import { Notice, Spinner } from './ui';
import { IconCard, IconLock } from './NavIcons';

export function PayButton({ payment, onPaid, className = '' }: { payment: Pick<Payment, 'id' | 'amount' | 'currency'>; onPaid: () => void; className?: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pay = async () => {
    setBusy(true);
    setError(null);
    try {
      if ((await payNow(payment.id)) === 'paid') onPaid();
    } catch (err) {
      setError(errorMessage(err, 'Payment could not be completed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={className}>
      <button type="button" disabled={busy} onClick={() => void pay()} className={BTN_PRIMARY} style={BTN_PRIMARY_STYLE}>
        {busy ? <Spinner /> : <IconCard className="h-4 w-4" />} Pay {formatMoney(payment.amount, payment.currency)}
      </button>
      {error && (
        <div className="mt-2">
          <Notice tone="rose">{error}</Notice>
        </div>
      )}
    </div>
  );
}

/** Shown in place of the rating result until the rating fee is paid. */
export function RatingPaywall({ fee, published, onPaid }: { fee: Payment | null; published: boolean; onPaid: () => void }) {
  return (
    <section
      className="mz-fade-up overflow-hidden rounded-2xl p-6"
      style={{ border: '1.5px solid #f9a8d4', background: 'linear-gradient(135deg,#fff5f8 0%,#ffffff 70%)', boxShadow: '0 12px 36px rgba(214,0,110,0.10)' }}
    >
      <div className="flex flex-wrap items-start gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#d6006e] text-white">
          <IconLock className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-black text-[#6b0030]">{published ? 'Your rating is ready — unlock it' : 'Rating fee'}</h2>
          <p className="mt-1 max-w-xl text-sm text-[#6b3a52]">
            {published
              ? 'Your Micus analyst has published your credit rating and the recommended actions to improve it. Complete the rating fee payment to view the full result.'
              : 'Your rating result opens as soon as your analyst publishes it and the rating fee is paid. You can pay now so nothing waits on you.'}
          </p>
          {fee && (
            <p className="mt-3 text-sm text-[#6b0030]">
              <span className="text-2xl font-black text-[#d6006e]">{formatMoney(fee.amount, fee.currency)}</span>
              <span className="ml-2 text-xs text-[#b0708f]">
                {fee.mode === 'DOMESTIC' ? 'UPI, cards, netbanking & wallets' : 'International card payment'} · secured by Razorpay
              </span>
            </p>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {fee && <PayButton payment={fee} onPaid={onPaid} />}
            <Link to="/payments" className="text-xs font-bold text-[#d6006e]">
              All payments →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
